import RemoteConnection from "@/components/RemoteConnection";
import { useEffect, useState } from "react";
import { Settings as SettingsIcon, Save, Globe, ShieldAlert, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, Loading } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [sites, setSites] = useState([]);
  const { canWrite } = useAuth();

  useEffect(() => {
    api.get("/settings").then(setSettings);
    api.get("/sites").then(setSites);
  }, []);

  const save = async () => {
    try {
      await api.patch("/settings", {
        wildcard_destination: settings.wildcard_destination || null,
        default_site: settings.default_site || null,
        mode: settings.mode,
        alert_disk_pct: Number(settings.alert_disk_pct),
        alert_cpu_pct: Number(settings.alert_cpu_pct),
        alert_mem_pct: Number(settings.alert_mem_pct),
        scheduled_backups: !!settings.scheduled_backups,
        backup_hour: Number(settings.backup_hour),
        custom_404_html: settings.custom_404_html || "",
      });
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!settings) return <Loading />;
  const NONE = "__none__";

  return (
    <div data-testid="settings-page">
      <PageHeader
        title="Settings"
        breadcrumb="cannon / settings"
        subtitle="Routing defaults, mode and alert thresholds"
        actions={canWrite && <button onClick={save} data-testid="save-settings-btn" className="inline-flex items-center gap-2 rounded-md bg-zinc-100 text-zinc-900 px-3 py-1.5 text-sm font-medium hover:bg-white"><Save className="h-4 w-4" /> Save</button>}
      />

      <div className="max-w-2xl space-y-4">
        <RemoteConnection />
        <div className="border border-zinc-800 rounded-lg bg-[#0c0c0e] p-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-4"><Globe className="h-3.5 w-3.5" /> Routing Defaults</div>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Wildcard Destination</label>
              <p className="text-xs text-zinc-600 mb-1.5 font-mono">Any unused *.studyformath.site resolves here</p>
              <Select value={settings.wildcard_destination || NONE} onValueChange={(v) => setSettings({ ...settings, wildcard_destination: v === NONE ? null : v })} disabled={!canWrite}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="wildcard-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="bg-[#0c0c0e] border-zinc-800"><SelectItem value={NONE}>None</SelectItem>{sites.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name} ({s.slug})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Default Site</label>
              <p className="text-xs text-zinc-600 mb-1.5 font-mono">Final fallback when nothing else matches</p>
              <Select value={settings.default_site || NONE} onValueChange={(v) => setSettings({ ...settings, default_site: v === NONE ? null : v })} disabled={!canWrite}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="default-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="bg-[#0c0c0e] border-zinc-800"><SelectItem value={NONE}>None</SelectItem>{sites.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name} ({s.slug})</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="border border-zinc-800 rounded-lg bg-[#0c0c0e] p-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-4"><ShieldAlert className="h-3.5 w-3.5" /> Operation</div>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Mode</label>
              <Select value={settings.mode} onValueChange={(v) => setSettings({ ...settings, mode: v })} disabled={!canWrite}>
                <SelectTrigger className="mt-1.5 bg-zinc-900 border-zinc-800" data-testid="mode-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0c0c0e] border-zinc-800"><SelectItem value="production">Production</SelectItem><SelectItem value="maintenance">Maintenance</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">CPU / RAM Alert Threshold (%)</label>
              <div className="flex gap-2 mb-4 mt-2"><Input aria-label="CPU alert threshold" type="number" min="1" max="100" value={settings.alert_cpu_pct??90} onChange={e=>setSettings({...settings,alert_cpu_pct:Number(e.target.value)})} disabled={!canWrite}/><Input aria-label="RAM alert threshold" type="number" min="1" max="100" value={settings.alert_mem_pct??90} onChange={e=>setSettings({...settings,alert_mem_pct:Number(e.target.value)})} disabled={!canWrite}/></div>
              <label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Disk Alert Threshold (%)</label>
              <Input type="number" value={settings.alert_disk_pct} onChange={(e) => setSettings({ ...settings, alert_disk_pct: e.target.value })} disabled={!canWrite} className="mt-1.5 bg-zinc-900 border-zinc-800 font-mono max-w-[120px]" data-testid="disk-threshold" />
            </div>
          </div>
        </div>

        <div className="border border-zinc-800 rounded-lg bg-[#0c0c0e] p-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-4"><CalendarClock className="h-3.5 w-3.5" /> Scheduled Backups</div>
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-md border border-zinc-800 px-3 py-2.5">
              <div>
                <div className="text-sm text-zinc-200">Nightly Back Up All</div>
                <div className="font-mono text-xs text-zinc-500 mt-0.5">Snapshots every site once a day · retention 20 (keep known-good)</div>
              </div>
              <Switch checked={!!settings.scheduled_backups} onCheckedChange={(v) => setSettings({ ...settings, scheduled_backups: v })} disabled={!canWrite} data-testid="scheduled-backups-switch" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Run at (hour, UTC)</label>
              <Select value={String(settings.backup_hour)} onValueChange={(v) => setSettings({ ...settings, backup_hour: v })} disabled={!canWrite || !settings.scheduled_backups}>
                <SelectTrigger className="mt-1.5 bg-zinc-900 border-zinc-800 max-w-[140px]" data-testid="backup-hour-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0c0c0e] border-zinc-800 max-h-64">
                  {Array.from({ length: 24 }).map((_, h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {settings.last_backup_run && <p className="text-xs text-zinc-600 font-mono">Last scheduled run: {settings.last_backup_run}</p>}
          </div>
        </div>

        <div className="border border-zinc-800 rounded-lg bg-[#0c0c0e] p-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-4"><SettingsIcon className="h-3.5 w-3.5" /> Custom 404 Page</div>
          <p className="text-xs text-zinc-500 font-mono mb-2">Shown by the live host router when a hostname matches no site. Leave blank for the default.</p>
          <textarea value={settings.custom_404_html || ""} onChange={(e) => setSettings({ ...settings, custom_404_html: e.target.value })} disabled={!canWrite}
            rows={5} placeholder="<h1>Not found</h1>" data-testid="custom-404"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm font-mono text-zinc-200 focus:outline-none focus:ring-1 focus:ring-zinc-600" />
        </div>
      </div>
    </div>
  );
}
